import React, { useEffect, useRef } from 'react';
import { createPortal } from 'react-dom';
import { DocSheetCloseButton } from '../../../DocsArea/DocSheetCloseButton';
import { formatWaitAvailabilityCopy } from '../ConditionsDropdown/utils/conditionWait';
import { findPreferredStatus } from '../../FlowchartView/utils/statusManagement';
import './styles.css';

const ClockMark = () => (
  <svg width="14" height="14" viewBox="0 0 24 24" fill="none" aria-hidden>
    <circle cx="12" cy="12" r="9" stroke="currentColor" strokeWidth="1.7" />
    <path d="M12 7.5V12l3 2" stroke="currentColor" strokeWidth="1.7" strokeLinecap="round" strokeLinejoin="round" />
  </svg>
);

const LockMark = () => (
  <svg width="14" height="14" viewBox="0 0 24 24" fill="none" aria-hidden>
    <rect x="5" y="11" width="14" height="10" rx="2" stroke="currentColor" strokeWidth="1.7" />
    <path d="M8 11V8a4 4 0 0 1 8 0v3" stroke="currentColor" strokeWidth="1.7" strokeLinecap="round" />
  </svg>
);

const Name = ({ children }) => (
  <span className="dep-warning-modal__name">{children}</span>
);

const StatusMark = ({ label, color }) => (
  <span
    className="dep-warning-modal__status"
    style={{
      '--status-color': color || '#22c55e',
      '--status-bg': `${color || '#22c55e'}20`,
    }}
  >
    {label}
  </span>
);

const WaitLead = ({ clauses, verb, completedStatus }) => (
  <>
    this mission can only be started{' '}
    {clauses.map((clause, i) => (
      <React.Fragment key={`${clause.name}-${i}`}>
        {i > 0 && (i === clauses.length - 1 ? ' and ' : ', ')}
        {clause.duration ? `${clause.duration} after ` : null}
        <Name>{clause.name}</Name>
      </React.Fragment>
    ))}
    {' '}{verb} been marked as{' '}
    <StatusMark label={completedStatus.name} color={completedStatus.color} />.
  </>
);

/**
 * Shown when someone tries to move a locked mission forward.
 */
const DependencyWarningModal = ({
  isOpen,
  blockingDependencies = [],
  actionLabel = 'check this off',
  missionName = '',
  targetStatusName = '',
  targetStatusColor = '',
  statuses = [],
  confirmLabel,
  onConfirm,
  onCancel,
  onGoToDependency,
  parentMissionName,
  parentCampaignName,
  onGoToParentMission,
}) => {
  const cancelBtnRef = useRef(null);

  useEffect(() => {
    if (isOpen && cancelBtnRef.current) {
      cancelBtnRef.current.focus();
    }
  }, [isOpen]);

  if (!isOpen) return null;

  const handleBackdropClick = (e) => {
    if (e.target === e.currentTarget) onCancel?.();
  };

  const handleKeyDown = (e) => {
    if (e.key === 'Escape') onCancel?.();
  };

  const handleDependencyClick = (dep) => {
    if (!onGoToDependency) return;
    onCancel?.();
    onGoToDependency(dep.id);
  };

  const handleParentMissionClick = () => {
    if (!onGoToParentMission) return;
    onCancel?.();
    onGoToParentMission();
  };

  const hasGoTo = !!onGoToDependency;
  const parentMissionWarning = !!parentMissionName;
  const campaignLabel = parentCampaignName || 'the parent campaign';
  const waitingDeps = blockingDependencies.filter((d) => d.reason === 'waiting_until');
  const otherDeps = blockingDependencies.filter((d) => d.reason !== 'waiting_until');
  const waitCopy = formatWaitAvailabilityCopy(waitingDeps);
  const isWaitOnly = waitCopy && otherDeps.length === 0 && !parentMissionWarning;
  const statusColor = targetStatusColor || '#a1a1aa';
  const preferredCompleted = findPreferredStatus(statuses, 'completed');
  const completedStatus = {
    name: preferredCompleted?.name || 'Done',
    color: preferredCompleted?.color || '#22c55e',
  };

  const maxVisible = 5;
  const hiddenCount = Math.max(0, otherDeps.length - maxVisible);
  const visibleOther = hiddenCount > 0 ? otherDeps.slice(0, maxVisible) : otherDeps;

  const modal = (
    <div
      className="dep-warning-modal__backdrop"
      data-sprint-overlay-root
      onClick={handleBackdropClick}
      onKeyDown={handleKeyDown}
      tabIndex={-1}
    >
      <div
        className="dep-warning-modal__card"
        role="dialog"
        aria-modal="true"
        aria-labelledby="dep-warning-title"
      >
        <header className="dep-warning-modal__bar">
          <div className="dep-warning-modal__bar-left">
            <span className="dep-warning-modal__mark" aria-hidden>
              {isWaitOnly ? <ClockMark /> : <LockMark />}
            </span>
            <h3 id="dep-warning-title" className="dep-warning-modal__title">
              {isWaitOnly
                ? waitCopy.title
                : "You can't start this mission yet"}
            </h3>
          </div>
          <DocSheetCloseButton onClose={onCancel} />
        </header>

        <div className="dep-warning-modal__body">
          <div className="dep-warning-modal__ask">
            <p className="dep-warning-modal__copy">
              You tried to change the status of: <Name>{missionName || 'this mission'}</Name>
            </p>
            <p className="dep-warning-modal__copy">
              To: <StatusMark label={targetStatusName || 'this status'} color={statusColor} />
            </p>
          </div>
          <p className="dep-warning-modal__copy">
            {parentMissionWarning ? (
              <>
                But this mission lives under{' '}
                <Name>{parentMissionName}</Name>
                {parentCampaignName ? <> in <Name>{parentCampaignName}</Name></> : ''}, which is still locked.
              </>
            ) : isWaitOnly ? (
              <>But <WaitLead clauses={waitCopy.clauses} verb={waitCopy.verb} completedStatus={completedStatus} /></>
            ) : waitCopy ? (
              <>
                But <WaitLead clauses={waitCopy.clauses} verb={waitCopy.verb} completedStatus={completedStatus} />
                {otherDeps.length > 0 ? ' You also still need to finish these first.' : ''}
              </>
            ) : (
              <>
                But before you can {actionLabel}, finish {otherDeps.length === 1 ? 'this mission' : 'these missions'} first.
              </>
            )}
          </p>

          {parentMissionWarning && onGoToParentMission && (
            <button
              type="button"
              className="dep-warning-modal__parent"
              onClick={handleParentMissionClick}
              aria-label={`Go to "${parentMissionName}" in "${campaignLabel}"`}
            >
              <span>Open {parentMissionName}</span>
              <svg width="14" height="14" viewBox="0 0 16 16" fill="none" aria-hidden>
                <path d="M6 4l4 4-4 4" stroke="currentColor" strokeWidth="1.5" strokeLinecap="round" strokeLinejoin="round" />
              </svg>
            </button>
          )}

          {!parentMissionWarning && !isWaitOnly && visibleOther.length > 0 && (
            <div className="dep-warning-modal__list">
              {visibleOther.map((dep) => (
                <button
                  key={dep.id}
                  type="button"
                  className={`dep-warning-modal__row ${hasGoTo ? 'dep-warning-modal__row--link' : ''}`}
                  onClick={() => handleDependencyClick(dep)}
                  disabled={!hasGoTo}
                >
                  <span className="dep-warning-modal__row-name">{dep.name}</span>
                  {hasGoTo && (
                    <svg className="dep-warning-modal__row-go" width="14" height="14" viewBox="0 0 16 16" fill="none" aria-hidden>
                      <path d="M6 4l4 4-4 4" stroke="currentColor" strokeWidth="1.5" strokeLinecap="round" strokeLinejoin="round" />
                    </svg>
                  )}
                </button>
              ))}
              {hiddenCount > 0 && (
                <p className="dep-warning-modal__more">+{hiddenCount} more</p>
              )}
            </div>
          )}

        </div>

        <footer className="dep-warning-modal__footer">
          <button
            ref={cancelBtnRef}
            type="button"
            className="dep-warning-modal__btn"
            onClick={onCancel}
          >
            Got it, Don't change
          </button>
          <button
            type="button"
            className="dep-warning-modal__btn dep-warning-modal__btn--quiet"
            onClick={(e) => {
              e.stopPropagation();
              onConfirm?.();
            }}
          >
            {confirmLabel || "Change it, I know what I'm doing"}
          </button>
        </footer>
      </div>
    </div>
  );

  return createPortal(modal, document.body);
};

export default DependencyWarningModal;
